import { useState, useEffect } from 'react'
import { metaAssetsAPI } from '../services/api'
import { Button, Card, Alert, Spinner, Select } from '../components/ui'

const ASSET_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'page', label: 'Page' },
  { value: 'ad_account', label: 'Ad Account' },
  { value: 'asset_group', label: 'Asset Group' },
  { value: 'app', label: 'App' },
  { value: 'instagram_account', label: 'Instagram Account' },
  { value: 'whatsapp_account', label: 'WhatsApp Account' },
]

const MetaAssetsPage = () => {
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selectedAssetType, setSelectedAssetType] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState(null)

  // Load assets on mount and when filters change
  useEffect(() => {
    loadAssets()
  }, [selectedAssetType, searchQuery])

  const loadAssets = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {}
      if (selectedAssetType) {
        params.asset_type = selectedAssetType
      }
      if (searchQuery) {
        params.search = searchQuery
      }

      const response = selectedAssetType
        ? await metaAssetsAPI.getAssetsByType(selectedAssetType, params)
        : await metaAssetsAPI.getAssets(params)

      if (response.data.success) {
        const assetList = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data?.data || []
        setAssets(assetList)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load meta assets')
      setAssets([])
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async () => {
    try {
      setSyncing(true)
      setError(null)
      setSuccess(null)

      const response = await metaAssetsAPI.syncAssets()

      if (response.data.success) {
        setSuccess('Meta assets synced successfully')
        await loadAssets()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sync meta assets')
      setTimeout(() => setError(null), 5000)
    } finally {
      setSyncing(false)
    }
  }

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this meta asset?')) {
      return
    }

    try {
      setDeletingId(assetId)
      setError(null)
      setSuccess(null)

      const response = await metaAssetsAPI.deleteAsset(assetId)

      if (response.data.success) {
        setSuccess('Meta asset deleted successfully')
        await loadAssets()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete meta asset')
      setTimeout(() => setError(null), 5000)
    } finally {
      setDeletingId(null)
    }
  }

  const assetTypeOptions = ASSET_TYPES.map(type => ({
    value: type.value,
    label: type.label
  }))

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Meta Assets</h1>
        <p className="text-gray-600 mt-1">
          Manage your Meta Business assets (Pages, Ad Accounts, WhatsApp Accounts, etc.)
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4 flex-1">
              <div className="md:w-1/3">
                <Select
                  id="asset-type-filter"
                  label="Filter by Type"
                  options={assetTypeOptions}
                  value={selectedAssetType}
                  onChange={(e) => setSelectedAssetType(e.target.value)}
                />
              </div>
              <div className="md:w-1/3">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  id="search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleSync}
              disabled={syncing}
              loading={syncing}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? 'Syncing...' : 'Sync Assets'}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-lg font-medium mb-2">No meta assets found</p>
              <p className="text-sm mb-4">
                {searchQuery || selectedAssetType
                  ? 'Try adjusting your filters or sync assets from Meta'
                  : 'Sync assets from Meta to get started'}
              </p>
              <Button variant="primary" onClick={handleSync} disabled={syncing}>
                Sync Assets
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Asset ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Asset Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Asset Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assets.map((asset) => (
                    <tr key={asset.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {asset.asset_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {asset.asset_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {asset.asset_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(asset.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDelete(asset.id)}
                          disabled={deletingId === asset.id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          {deletingId === asset.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

export default MetaAssetsPage

